// src/pages/OrganizationSettings.js
import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import { toast } from "react-toastify";
import { getOrganization, updateOrganization } from "../Api";
import { getOrganizationId } from "../utils/authUtils";
import "./OrganizationSettings.css";

export default function OrganizationSettings() {
  const [organization, setOrganization] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  useEffect(() => {
    fetchOrganization();
  }, []);

  const fetchOrganization = async () => {
    setIsLoading(true);
    try {
      const orgId = getOrganizationId();
      if (!orgId) {
        toast.error("Organization ID not found");
        return;
      }

      const data = await getOrganization(orgId);
      setOrganization(data);
      setFormData({
        name: data.name || "",
        description: data.description || "",
      });
    } catch (error) {
      console.error("Failed to fetch organization:", error);
      toast.error("Failed to load organization details");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form to original values
    setFormData({
      name: organization.name || "",
      description: organization.description || "",
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Organization name is required");
      return;
    }

    setIsSaving(true);
    try {
      const orgId = getOrganizationId();
      const updateData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
      };

      const updated = await updateOrganization(orgId, updateData);
      setOrganization(updated);
      setIsEditing(false);
      toast.success("Organization updated successfully");
    } catch (error) {
      console.error("Failed to update organization:", error);
      toast.error("Failed to update organization");
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const handleCopyId = () => {
    if (organization?.organization_id) {
      navigator.clipboard.writeText(organization.organization_id);
      toast.success("Organization ID copied to clipboard");
    }
  };

  const formatKey = (key) => {
    return key
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="app-container">
      <Sidebar />
      <main className="main-section">
        <div className="settings-page-container">
          
          {isLoading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <span>Loading organization details...</span>
            </div>
          ) : !organization ? (
            <div className="error-state">
              <div className="error-icon">!</div>
              <h3>Failed to load organization</h3>
              <p>Please try refreshing the page or contact support.</p>
            </div>
          ) : (
            <div className="settings-content">
              
              {/* Minimal Header */}
              <div className="org-header-minimal">
                <div className="org-logo-wrapper">
                  <div className="org-logo">
                    {organization.name ? organization.name.charAt(0).toUpperCase() : 'O'}
                  </div>
                </div>
                <div className="org-identity-minimal">
                  <h1 className="org-name">{organization.name}</h1>
                  <div className="org-id-row">
                    <span className="org-id-label">ID:</span>
                    <code className="org-id-value">{organization.organization_id}</code>
                    <button 
                      className="btn-copy-minimal" 
                      onClick={handleCopyId}
                      title="Copy ID"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  </div>
                  <div className="org-actions">
                    {!isEditing && (
                      <button className="btn-edit-minimal" onClick={handleEdit}>
                        Edit Details
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Main Content Grid */}
              <div className="org-grid">
                
                {/* General Info */}
                <div className="settings-card">
                  <div className="card-header">
                    <h3>General Information</h3>
                  </div>
                  <div className="card-body">
                    {isEditing ? (
                      <div className="edit-form">
                        <div className="form-group">
                          <label htmlFor="orgName">Organization Name</label>
                          <input
                            id="orgName"
                            type="text"
                            className="form-input-modern"
                            value={formData.name}
                            onChange={(e) => handleInputChange("name", e.target.value)}
                            placeholder="e.g. Acme Corp"
                            disabled={isSaving}
                          />
                        </div>

                        <div className="form-group">
                          <label htmlFor="orgDescription">Description</label>
                          <textarea
                            id="orgDescription"
                            className="form-textarea-modern"
                            rows="4"
                            value={formData.description}
                            onChange={(e) => handleInputChange("description", e.target.value)}
                            placeholder="Briefly describe your organization..."
                            disabled={isSaving}
                          />
                        </div>

                        <div className="form-actions">
                          <button
                            className="btn-cancel"
                            onClick={handleCancel}
                            disabled={isSaving}
                          >
                            Cancel
                          </button>
                          <button
                            className="btn-save"
                            onClick={handleSave}
                            disabled={isSaving}
                          >
                            {isSaving ? "Saving..." : "Save Changes"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="view-list">
                        <div className="view-row">
                          <span className="view-label">Created On</span>
                          <span className="view-value">{formatDate(organization.created_at)}</span>
                        </div>
                        <div className="view-row">
                          <span className="view-label">Description</span>
                          <span className="view-value description-text">
                            {organization.description || <span className="text-muted">No description provided</span>}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Metadata Card */}
                {organization.metadata && (
                  <div className="settings-card">
                    <div className="card-header">
                      <h3>Metadata</h3>
                    </div>
                    <div className="card-body">
                      <div className="metadata-list">
                        {Object.entries(organization.metadata).map(([key, value]) => (
                          <div key={key} className="metadata-row">
                            <span className="meta-key">{formatKey(key)}</span>
                            <span className="meta-value">{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </main>
    </div>
  );
}
